import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Edit, Trash2, Eye, MoreHorizontal, Settings, DollarSign, ListTodo, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useServices, useDeleteService } from '@/hooks/use-services';
import { useToast } from '@/hooks/use-toast';
import { DeleteAlertDialog } from '@/components/admin/DeleteAlertDialog';
import type { Service } from '@/data/projects';
import { Skeleton } from '@/components/ui/skeleton';

export default function AdminServicos() {
  const [searchTerm, setSearchTerm] = useState('');
  const { data: services = [], isLoading, error } = useServices();
  const deleteService = useDeleteService();
  const { toast } = useToast();

  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.summary.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteTitle, setDeleteTitle] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const confirmDelete = (id: string, name: string) => {
    setDeleteId(id);
    setDeleteTitle(name);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (deleteId) {
      try {
        await deleteService.mutateAsync(deleteId);
        toast({
          title: "Serviço excluído",
          description: `O serviço "${deleteTitle}" foi excluído com sucesso.`,
        });
        setDeleteDialogOpen(false);
      } catch (error) {
        toast({
          title: "Erro ao excluir",
          description: "Ocorreu um erro ao excluir o serviço.",
          variant: "destructive",
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <Skeleton className="h-8 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 text-center py-20">
        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <Settings className="h-8 w-8 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold text-red-900">Erro ao carregar serviços</h2>
        <p className="text-red-700">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-10">
      {/* Premium Sticky Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md pb-4 pt-2 border-b mb-6 -mx-6 px-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Serviços</h1>
          <p className="text-muted-foreground hidden md:block">
            Gerencie os serviços oferecidos e seus detalhes
          </p>
        </div>
        <Button asChild className="shadow-lg bg-blue-600 hover:bg-blue-700 transition-all">
          <Link to="/admin/servicos/novo">
            <Plus className="mr-2 h-4 w-4" />
            Novo Serviço
          </Link>
        </Button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="border-0 shadow-lg overflow-hidden backdrop-blur-sm bg-card/50">
          <CardHeader className="border-b bg-muted/20 pb-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">

              {/* Search Bar */}
              <div className="relative flex-1 max-w-md group">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground group-focus-within:text-blue-500 transition-colors" />
                <Input
                  placeholder="Buscar serviços..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-background border-muted group-focus-within:border-blue-500 transition-colors"
                />
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Filter className="h-4 w-4" />
                <span>{filteredServices.length} resultados</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="w-[300px] pl-6">Serviço</TableHead>
                  <TableHead>Preço Inicial</TableHead>
                  <TableHead>Detalhes</TableHead>
                  <TableHead className="text-right pr-6">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <AnimatePresence>
                  {filteredServices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center h-40 text-muted-foreground">
                        <div className="flex flex-col items-center justify-center">
                          <div className="h-12 w-12 bg-muted rounded-full flex items-center justify-center mb-3">
                            <Settings className="h-6 w-6 text-muted-foreground/50" />
                          </div>
                          <p className="font-medium">Nenhum serviço encontrado</p>
                          <p className="text-sm text-muted-foreground/70">
                            Adicione serviços para começar.
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredServices.map((service, index) => (
                      <motion.tr
                        key={service.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ delay: index * 0.05 }}
                        className="group hover:bg-muted/40 transition-colors border-b border-border/50 last:border-0"
                        style={{ display: 'table-row' }}
                        layoutId={service.id}
                      >
                        <TableCell className="pl-6 py-4">
                          <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-600 ring-1 ring-blue-500/20 group-hover:bg-blue-500/20 transition-all shrink-0">
                              <Settings className="h-6 w-6" />
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="font-semibold text-foreground group-hover:text-blue-600 transition-colors text-base">{service.name}</span>
                              <span className="text-xs text-muted-foreground truncate max-w-[250px]">{service.summary}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 font-medium text-green-600 bg-green-500/10 px-2 py-1 rounded w-fit text-sm border border-green-500/20">
                            <DollarSign className="h-3 w-3" />
                            {service.startingPrice || 'Sob consulta'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Badge variant="secondary" className="font-normal text-xs bg-muted hover:bg-muted/80">
                              {service.deliverables?.length || 0} entregáveis
                            </Badge>
                            <Badge variant="outline" className="font-normal text-xs border-dashed">
                              {service.process?.length || 0} etapas
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-right pr-6">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-background">
                                  <span className="sr-only">Abrir menu</span>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Ações</DropdownMenuLabel>
                                <DropdownMenuItem asChild className="cursor-pointer">
                                  <Link to={`/admin/servicos/${service.id}/editar`}>
                                    <Edit className="mr-2 h-4 w-4 text-blue-500" />
                                    Editar
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => confirmDelete(service.id, service.name)}
                                  className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </motion.tr>
                    ))
                  )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </motion.div>

      <DeleteAlertDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={`Excluir "${deleteTitle}"?`}
        description="Esta ação não pode ser desfeita. Isso excluirá permanentemente o serviço."
        onConfirm={handleDelete}
        isDeleting={deleteService.isPending}
      />
    </div >
  );
}
